import { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { AIDropdownMinimal } from '@app/components/AIDropdown/AIDropdownMinimal';
import { VariableDropdown } from '@app/components/VariableDropdown/VariableDropdown';
import { 
  improveText, 
  changeTone,
  fixGrammarAndSpelling, 
  makeTextConcise, 
  regenerateText 
} from '@app/services/service';
import ChangeToneIcon from '@app/assets/icons/ai/change-tone.svg?react';
import ConciseIcon from '@app/assets/icons/ai/concise.svg?react';
import FormatIcon from '@app/assets/icons/ai/format.svg?react';
import GrammarIcon from '@app/assets/icons/ai/grammar.svg?react';
import RegenerateIcon from '@app/assets/icons/ai/regenerate.svg?react';
import Edit03Icon from '@app/assets/icons/edit-03.svg?react';

interface FeedbackFormContent {
  tags: string[];
  inputLabel: string;
  referenceContacts: boolean;
  buttonText: string;
  buttonColor: string;
}

// Helper function to insert variable at cursor position
const insertVariableAtCursor = (
  currentValue: string,
  variable: string,
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>
): { newValue: string; cursorPosition: number } => {
  if (inputRef.current) {
    const input = inputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = currentValue.slice(0, start) + variable + currentValue.slice(end);
    const cursorPosition = start + variable.length;
    return { newValue, cursorPosition };
  }
  const newValue = currentValue + variable;
  return { newValue, cursorPosition: newValue.length };
};

const defaultFeedbackFormContent: FeedbackFormContent = {
  tags: ['Navigation system', 'Unpleasant design', 'Limited features', 'Technical issues', 'Lack of information', 'Payment problems', 'Inadequate support', 'Other'],
  inputLabel: 'What can we do to improve your experience?',
  referenceContacts: true,
  buttonText: 'Send direct feedback',
  buttonColor: '#52B6FE'
};

interface FeedbackFormDesignEditorProps {
  content: FeedbackFormContent;
  onChange: (content: FeedbackFormContent) => void;
}

export const FeedbackFormDesignEditor = ({ content, onChange }: FeedbackFormDesignEditorProps) => {
  const [newTagInput, setNewTagInput] = useState('');
  const inputLabelRef = useRef<HTMLInputElement>(null);
  const buttonTextRef = useRef<HTMLInputElement>(null);

  const handleInputLabelVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(content.inputLabel, variable, inputLabelRef);
    onChange({ ...content, inputLabel: newValue });
    setTimeout(() => {
      if (inputLabelRef.current) {
        inputLabelRef.current.setSelectionRange(cursorPosition, cursorPosition);
        inputLabelRef.current.focus();
      }
    }, 0);
  };

  const handleButtonTextVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(content.buttonText, variable, buttonTextRef);
    onChange({ ...content, buttonText: newValue });
    setTimeout(() => {
      if (buttonTextRef.current) {
        buttonTextRef.current.setSelectionRange(cursorPosition, cursorPosition);
        buttonTextRef.current.focus();
      }
    }, 0);
  };

  // Load saved content when component mounts
  useEffect(() => {
    const storedData = localStorage.getItem('campaignFormData');
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (parsedData.feedbackFormDesignContent) {
          // Merge with default values to ensure all new fields exist
          const savedContent = {
            ...defaultFeedbackFormContent,
            ...parsedData.feedbackFormDesignContent
          };
          onChange(savedContent);
        }
      } catch (error) {
        // If parsing fails, keep default values
        console.error('Error parsing stored feedback form data:', error);
      }
    }
  }, [onChange]);

  // Handle adding new tags
  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newTagInput.trim()) {
      e.preventDefault();
      const newTag = newTagInput.trim();
      if (!content.tags.includes(newTag)) {
        onChange({ ...content, tags: [...content.tags, newTag] });
        setNewTagInput('');
      }
    }
  };

  const handleTagAIAction = async (action: () => Promise<any>) => {
    if (!newTagInput.trim()) return;
    
    try {
      const response = await action();
      if (response?.data?.text) {
        setNewTagInput(response.data.text);
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  const handleFeedbackAIAction = async (action: () => Promise<any>, field: 'inputLabel' | 'buttonText') => {
    const value = field === 'inputLabel' ? content.inputLabel : content.buttonText;
    if (!value.trim()) return;
    
    try {
      const response = await action();
      if (response?.data?.text) {
        onChange({ ...content, [field]: response.data.text });
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  // Helper function to create tone options for Change tone dropdown
  const createToneOptions = (value: string, handleAIAction: (action: () => Promise<any>) => Promise<void>) => {
    const tones = [
      { label: 'Write appreciative', tone: 'appreciative' },
      { label: 'Write apologetic', tone: 'apologetic' },
      { label: 'Write informative', tone: 'informative' },
      { label: 'Write interactive', tone: 'interactive' },
      { label: 'Write humorous', tone: 'humorous' },
      { label: 'Write formal', tone: 'formal' },
      { label: 'Write casual', tone: 'casual' },
      { label: 'Write personalized', tone: 'personalized' }
    ];

    return tones.map(({ label, tone }) => ({
      label,
      Icon: <Edit03Icon className="w-4 h-4" />,
      onClick: () => handleAIAction(() => changeTone(value, tone))
    }));
  };

  const tagsAIActions = [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleTagAIAction(() => regenerateText(newTagInput))
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(newTagInput, handleTagAIAction)
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleTagAIAction(() => fixGrammarAndSpelling(newTagInput))
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleTagAIAction(() => makeTextConcise(newTagInput))
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleTagAIAction(() => improveText(newTagInput))
    }
  ];

  const inputLabelAIActions = [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleFeedbackAIAction(() => regenerateText(content.inputLabel), 'inputLabel')
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(content.inputLabel, (action) => handleFeedbackAIAction(action, 'inputLabel'))
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleFeedbackAIAction(() => fixGrammarAndSpelling(content.inputLabel), 'inputLabel')
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleFeedbackAIAction(() => makeTextConcise(content.inputLabel), 'inputLabel')
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleFeedbackAIAction(() => improveText(content.inputLabel), 'inputLabel')
    }
  ];

  const buttonTextAIActions = [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleFeedbackAIAction(() => regenerateText(content.buttonText), 'buttonText')
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(content.buttonText, (action) => handleFeedbackAIAction(action, 'buttonText'))
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleFeedbackAIAction(() => fixGrammarAndSpelling(content.buttonText), 'buttonText')
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleFeedbackAIAction(() => makeTextConcise(content.buttonText), 'buttonText')
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleFeedbackAIAction(() => improveText(content.buttonText), 'buttonText')
    }
  ];

  return (
    <div className="space-y-6">
      {/* Checkbox for elements staying the same */}
      <div className="flex items-center gap-2">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 9L9 12.375M9 6.49841V6.46875M2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.7279 2.25 15.75 5.27208 15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9Z" stroke="#B7BDCC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span className="text-sm text-gray-700">Elements within the 'Review' window stay the same.</span>
      </div>

      {/* Divider Line */}
      <div className="border-b border-gray-200 mt-4 mb-6"></div>

      {/* Tags Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tags to describe the experience</label>
        <div className="flex items-start gap-2">
          <input
            type="text"
            value={newTagInput}
            onChange={(e) => setNewTagInput(e.target.value)}
            onKeyDown={handleAddTag}
            placeholder="Write and press enter"
            className="w-[26rem] p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          />
          <div className="relative">
            <AIDropdownMinimal     
              actions={tagsAIActions}
              onlyIcon={true}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {content.tags.map((tag, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full text-sm"
            >
              <span>{tag}</span>
              <button
                onClick={() => {
                  const newTags = content.tags.filter((_, i) => i !== index);
                  onChange({ ...content, tags: newTags });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Input Label Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Input label</label>
        <div className="flex items-start gap-2">
          <input
            ref={inputLabelRef}
            type="text"
            value={content.inputLabel}
            onChange={(e) => onChange({ ...content, inputLabel: e.target.value })}
            className="w-[26rem] p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="What can we do to improve your experience?"
          />
          <div className="flex gap-1 items-center">
            <VariableDropdown onSelect={handleInputLabelVariableSelect} />
            <div className="relative">
              <AIDropdownMinimal     
                actions={inputLabelAIActions}
                onlyIcon={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Divider Line */}
      <div className="border-b border-gray-200 mt-4 mb-6"></div>

      {/* Reference Contacts Field */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <div 
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              content.referenceContacts ? 'bg-[#52B6FE]' : 'bg-[#F6F7FA]'
            }`}
            onClick={() => onChange({ ...content, referenceContacts: !content.referenceContacts })}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                content.referenceContacts ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </div>
          <span className="text-sm font-medium text-gray-700">Reference contacts</span>
        </label>
      </div>

      {/* Divider Line */}
      <div className="border-b border-gray-200 mt-4 mb-6"></div>

      {/* Button Text Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Text on button</label>
        <div className="flex items-start gap-2">
          <input
            ref={buttonTextRef}
            type="text"
            value={content.buttonText}
            onChange={(e) => onChange({ ...content, buttonText: e.target.value })}
            className="w-[26rem] p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder="Send direct feedback"
          />
          <div className="flex gap-1 items-center">
            <VariableDropdown onSelect={handleButtonTextVariableSelect} />
            <div className="relative">
              <AIDropdownMinimal     
                actions={buttonTextAIActions}
                onlyIcon={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Button Color Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Button color</label>
        <div className="space-y-3">
          <input
            type="text"
            value={content.buttonColor}
            onChange={(e) => onChange({ ...content, buttonColor: e.target.value })}
            placeholder="Custom #HEX"
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          />
          <div className="flex gap-2 flex-wrap">
            {['#52B6FE', '#000000', '#00B2FF', '#28a745', '#ffc107', '#dc3545', '#e83e8c', '#6f42c1', '#ffffff'].map((color) => (
              <button
                key={color}
                onClick={() => onChange({ ...content, buttonColor: color })}
                className={clsx(
                  'w-8 h-8 border-2 transition-colors rounded-md',
                  content.buttonColor === color ? 'border-blue-500' : 'border-gray-300'
                )}
                style={{ backgroundColor: color }}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
